/** The fields ranking reads; any custom rule row satisfies it. */
type RankableRule = {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
};

type RulesAheadOfOrganizationAgents<T> = {
  /** cubic.yaml rules in file order: the repository's file first, then the organization's. */
  yamlRules: T[];
  /** Dashboard agents that belong to this repository, newest first. */
  repositoryRules: T[];
  maxCustomRules: number;
};

export type RankRulesForCapParams<T extends RankableRule> = RulesAheadOfOrganizationAgents<T> & {
  /** This repository's rows of organization agents, oldest agent first. */
  organizationRules: T[];
};

export type RankedRules<T> = {
  /** The rules a review runs, in priority order. */
  rules: T[];
  /** Enabled organization agent rows left out because the repository hit the agent limit. */
  notRunningOrganizationRuleIds: string[];
};

export const ruleIdentity = (rule: Pick<RankableRule, 'title' | 'description'>) =>
  JSON.stringify([rule.title.trim(), rule.description.trim()]);

/**
 * The rules a review runs on a repository ahead of every organization agent, cut to the agent
 * limit: cubic.yaml rules, which are always enabled, then enabled repository agents. Needs no
 * instructions, so callers counting reach across many repositories can rank rows read without
 * them.
 */
export function rankRulesAheadOfOrganizationAgents<T extends Pick<RankableRule, 'enabled'>>({
  yamlRules,
  repositoryRules,
  maxCustomRules,
}: RulesAheadOfOrganizationAgents<T>): T[] {
  // YAML definitions take precedence but still respect the global cap.
  const rules = yamlRules.slice(0, maxCustomRules);
  // Whatever room remains is filled by UI-managed rules so legacy setups stay functional.
  let remainingSlots = Math.max(maxCustomRules - rules.length, 0);

  for (const rule of repositoryRules) {
    if (remainingSlots === 0) {
      break;
    }
    if (rule.enabled) {
      rules.push(rule);
      remainingSlots -= 1;
    }
  }

  return rules;
}

/**
 * The rules a review runs on a repository, cut to the agent limit, in this order:
 * 1. cubic.yaml rules, which are always enabled;
 * 2. enabled repository agents;
 * 3. enabled organization agents, oldest first, so a new agent never pushes out an older one.
 *
 * An organization agent with the same title and description as a rule that already runs is
 * skipped, so the same agent never runs twice. It isn't reported as not running, because its
 * twin runs.
 */
export function rankRulesForCap<T extends RankableRule>({
  yamlRules,
  repositoryRules,
  organizationRules,
  maxCustomRules,
}: RankRulesForCapParams<T>): RankedRules<T> {
  const rules = rankRulesAheadOfOrganizationAgents({ yamlRules, repositoryRules, maxCustomRules });
  let remainingSlots = Math.max(maxCustomRules - rules.length, 0);

  const runningIdentities = new Set(rules.map(ruleIdentity));
  const notRunningOrganizationRuleIds: string[] = [];

  for (const rule of organizationRules) {
    if (!rule.enabled || runningIdentities.has(ruleIdentity(rule))) {
      continue;
    }
    if (remainingSlots === 0) {
      notRunningOrganizationRuleIds.push(rule.id);
      continue;
    }
    rules.push(rule);
    runningIdentities.add(ruleIdentity(rule));
    remainingSlots -= 1;
  }

  return { rules, notRunningOrganizationRuleIds };
}
